import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

import type { ObjectId } from '../types'

/**
 * Centralises the UA Insights *game page* in-page navigation contract — the
 * `tab` / `motivation` / `pattern` / `lens` query params shared by the page
 * shell (`UAInsightsGamePage`) and its tab bodies (`GameOverviewTab`,
 * `GameStoryPatternsTab`). `motivation` and `lens` hold motivation ids and
 * `pattern` holds a pattern or sub-pattern id.
 *
 * Every mutation pushes a new history entry (`replace: false`) so browser
 * back/forward steps through drill-downs.
 *
 * Note: the *dialog* stack (tag / ad / compare dialogs, network/month filters)
 * is owned separately by `useUAInsightsDialogParams` / `useUAInsightsNavigation`;
 * this hook only governs the game page's own tab + drill-down params.
 */
export const useGamePageNavigation = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  /** Active motivation drill-down (Motivations tab), or null. */
  const activeMotivationId = searchParams.get('motivation')
  /** Active story-pattern drill-down (Story Patterns tab), or null. */
  const activePatternId = searchParams.get('pattern')
  /** Optional motivation lens applied to the story-pattern drill-down, or null. */
  const activeLensId = searchParams.get('lens')

  // Shared mutation primitive: derive a fresh `URLSearchParams` from the latest
  // snapshot, let the caller mutate it, and push it as a new history entry.
  const update = useCallback(
    (mutator: (next: URLSearchParams) => void) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          mutator(next)
          return next
        },
        { replace: false }
      )
    },
    [setSearchParams]
  )

  // Jump to a tab from the Overview's at-a-glance summary, dropping any
  // drill-down so the target tab opens at its top-level view.
  const goToTab = useCallback(
    (tab: string) => {
      update((next) => {
        next.set('tab', tab)
        next.delete('motivation')
        next.delete('pattern')
        next.delete('lens')
      })
    },
    [update]
  )

  // Open the in-page Motivation view on the Motivations tab. Pins the
  // Motivations tab explicitly (Overview is the default tab) and clears the
  // Story Patterns drill-down.
  const openMotivation = useCallback(
    (motivationId: ObjectId) => {
      update((next) => {
        next.set('tab', 'motivations')
        next.delete('pattern')
        next.delete('lens')
        next.set('motivation', motivationId)
      })
    },
    [update]
  )

  // Return from the Motivation view to the Motivations tab's charts/diagnosis.
  const backToMotivations = useCallback(() => {
    update((next) => next.delete('motivation'))
  }, [update])

  // Jump to the Story Patterns tab from the Overview, opening the table view of
  // the given pattern (clears any motivation drill-down / lens).
  const goToPattern = useCallback(
    (patternId: ObjectId) => {
      update((next) => {
        next.set('tab', 'story-patterns')
        next.set('pattern', patternId)
        next.delete('lens')
        next.delete('motivation')
      })
    },
    [update]
  )

  // Drill into a pattern while already on the Story Patterns tab, optionally
  // through a motivation lens.
  const openPatternInTab = useCallback(
    (patternId: ObjectId, lensMotivationId?: ObjectId | null) => {
      update((next) => {
        next.set('pattern', patternId)
        if (lensMotivationId) next.set('lens', lensMotivationId)
        else next.delete('lens')
      })
    },
    [update]
  )

  // Return from a Story Patterns drill-down to the table.
  const backToPatternTable = useCallback(() => {
    update((next) => {
      next.delete('pattern')
      next.delete('lens')
    })
  }, [update])

  // Drill from the Motivation view into one of its story patterns: hand off to
  // the Story Patterns tab with the motivation carried as the pattern's lens.
  const openPatternFromMotivation = useCallback(
    (patternId: ObjectId, lensMotivationId: ObjectId) => {
      update((next) => {
        next.delete('motivation')
        next.set('tab', 'story-patterns')
        next.set('pattern', patternId)
        next.set('lens', lensMotivationId)
      })
    },
    [update]
  )

  return useMemo(
    () => ({
      activeMotivationId,
      activePatternId,
      activeLensId,
      goToTab,
      openMotivation,
      backToMotivations,
      goToPattern,
      openPatternInTab,
      backToPatternTable,
      openPatternFromMotivation,
    }),
    [
      activeMotivationId,
      activePatternId,
      activeLensId,
      goToTab,
      openMotivation,
      backToMotivations,
      goToPattern,
      openPatternInTab,
      backToPatternTable,
      openPatternFromMotivation,
    ]
  )
}
